import { useState, useEffect, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useMagnum } from '@/context/MagnumContext'
import { apiFetch } from '@/lib/apiFetch'
import { useExerciseSession } from '@/hooks/useExerciseSession'
import { fetchMagnumMoment } from '@/lib/magnumMoment'
import SpellingInput from '@/components/SpellingInput'
import EffectRenderer from '@/components/EffectRenderer'
import type { EffectData } from '@/lib/magnumMoment'

type GameState = 'idle' | 'loading' | 'prompting' | 'input' | 'result' | 'done'

interface SpellingWordEntry {
  id: number
  text: string
  hint: string | null
  source: string
  dailyCorrect: number
  dailyFailed: boolean
  dailyCleared: boolean
  mastered: boolean
  totalAttempts: number
}

const DAILY_GOAL = 3

function speakWord(text: string) {
  if ('speechSynthesis' in window) {
    window.speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.lang = 'en-US'
    utterance.rate = 0.85
    window.speechSynthesis.speak(utterance)
  }
}

function diffLetters(attempted: string, correct: string): Array<{ letter: string; correct: boolean; expected: string }> {
  const result: Array<{ letter: string; correct: boolean; expected: string }> = []
  const maxLen = Math.max(attempted.length, correct.length)
  for (let i = 0; i < maxLen; i++) {
    const a = attempted[i] || ''
    const c = correct[i] || ''
    result.push({ letter: a, correct: a === c, expected: c })
  }
  return result
}

export default function SpellingGamePage() {
  const { user } = useAuth()
  const magnum = useMagnum()
  const { sessionId, activeMs, isIdle, startSession, endSession, dismissIdle } =
    useExerciseSession({ exerciseType: 'SPELLING' })
  const [localStats, setLocalStats] = useState({ correct: 0, incorrect: 0, total: 0, streak: 0 })

  const [state, setState] = useState<GameState>('idle')
  const [allWords, setAllWords] = useState<SpellingWordEntry[]>([])
  const [queue, setQueue] = useState<SpellingWordEntry[]>([])
  const [currentWord, setCurrentWord] = useState<SpellingWordEntry | null>(null)
  const [listName, setListName] = useState('')
  const [lastAttempt, setLastAttempt] = useState<string | null>(null)
  const [lastCorrect, setLastCorrect] = useState<boolean | null>(null)
  const [masteredCount, setMasteredCount] = useState(0)
  const [inputKey, setInputKey] = useState(0)
  const [activeEffects, setActiveEffects] = useState<EffectData[] | null>(null)
  const wordStartTime = useRef(0)

  useEffect(() => {
    magnum.setFrameIdle(isIdle)
    if (isIdle) {
      fetchMagnumMoment({ contextType: 'IDLE_RETURN', exerciseType: 'SPELLING',
      category: 'LANGUAGE', streak: localStats.streak, sessionId, activeMs })
        .then((m) => magnum.handleEngineResponse(m))
        .catch(() => {})
    }
  }, [isIdle])

  const startGame = async () => {
    setState('loading')
    try {
      const [data] = await Promise.all([
        apiFetch('/spelling/words'),
        startSession(),
      ])
      const words: SpellingWordEntry[] = data.words
      setAllWords(words)
      setListName(data.listName || 'Spelling')
      const needsPractice = words.filter(w => !w.mastered && !w.dailyCleared)
      const q = needsPractice.length > 0 ? shuffle(needsPractice) : shuffle(words.filter(w => !w.mastered))
      setQueue(q)
      setMasteredCount(words.filter(w => w.mastered).length)
      advanceWord(q)

      fetchMagnumMoment({ contextType: 'GAME_STARTED', exerciseType: 'SPELLING',
      category: 'LANGUAGE', streak: 0, sessionId })
        .then((m) => magnum.handleEngineResponse(m, 'happy'))
        .catch(() => {})
    } catch {
      setState('idle')
    }
  }

  const advanceWord = (q: SpellingWordEntry[]) => {
    if (q.length === 0) {
      handleEndSession()
      return
    }
    const next = q[0]
    setCurrentWord(next)
    setQueue(q.slice(1))
    setLastAttempt(null)
    setLastCorrect(null)
    setInputKey(prev => prev + 1)
    setState('prompting')
    wordStartTime.current = Date.now()
    setTimeout(() => speakWord(next.text), 300)
  }

  const handleSubmit = useCallback(async (attempted: string) => {
    if (!currentWord || state !== 'input') return

    const responseMs = Date.now() - wordStartTime.current
    const correct = attempted.toLowerCase() === currentWord.text.toLowerCase()

    setLastAttempt(attempted)
    setLastCorrect(correct)
    setState('result')

    const newStreak = correct ? localStats.streak + 1 : 0
    setLocalStats(prev => ({
      correct: prev.correct + (correct ? 1 : 0),
      incorrect: prev.incorrect + (correct ? 0 : 1),
      total: prev.total + 1,
      streak: newStreak,
    }))

    const contextType = correct ? 'CORRECT_ANSWER' : 'WRONG_ANSWER'

    fetchMagnumMoment({
      contextType,
      exerciseType: 'SPELLING',
      category: 'LANGUAGE',
      wordText: currentWord.text,
      correct,
      responseMs,
      activeMs,
      streak: newStreak,
      sessionId,
    }).then((moment) => {
      magnum.handleEngineResponse(moment, correct ? 'happy' : 'encouraging')
      if (moment.effects?.length) setActiveEffects(moment.effects)
    }).catch(() => {})

    // Record via spelling endpoint (handles mastery + exercise_attempt with placeholder wordId)
    try {
      const result = await apiFetch('/spelling/attempt', {
        method: 'POST',
        body: JSON.stringify({
          spellingWordId: currentWord.id,
          correct,
          responseMs,
          attemptedSpelling: attempted,
          sessionId,
        }),
      })

      if (result.mastered && !currentWord.mastered) {
        setMasteredCount(prev => prev + 1)
        setAllWords(prev => prev.map(w => w.id === currentWord.id ? { ...w, mastered: true } : w))
      }

      setCurrentWord(prev => prev ? {
        ...prev,
        dailyCorrect: result.dailyCorrect,
        dailyFailed: result.dailyFailed,
        dailyCleared: result.dailyCleared,
        mastered: result.mastered,
      } : prev)
    } catch {}
  }, [currentWord, state, localStats.streak, sessionId, activeMs])

  const handleNext = useCallback(() => {
    if (!currentWord) return

    let newQueue = [...queue]

    if (lastCorrect) {
      // If daily goal not yet cleared and not fully mastered, shuffle back in
      if (!currentWord.dailyCleared && !currentWord.mastered) {
        const insertAt = Math.min(newQueue.length, 2 + Math.floor(Math.random() * 3))
        newQueue.splice(insertAt, 0, currentWord)
      }
    } else {
      // Wrong: shuffle back in soon (still practice even though daily mastery is lost)
      if (!currentWord.mastered) {
        const insertAt = Math.min(newQueue.length, 1 + Math.floor(Math.random() * 2))
        newQueue.splice(insertAt, 0, currentWord)
      }
    }

    // Check if all words are mastered
    const totalMastered = allWords.filter(w => w.mastered).length +
      (currentWord.mastered && !allWords.find(w => w.id === currentWord.id)?.mastered ? 1 : 0)

    if (totalMastered >= allWords.length) {
      handleEndSession()
      return
    }

    advanceWord(newQueue)
  }, [currentWord, lastCorrect, queue, allWords])

  const handleEndSession = async () => {
    setState('done')
    fetchMagnumMoment({ contextType: 'SESSION_END', exerciseType: 'SPELLING',
      category: 'LANGUAGE', streak: localStats.streak, sessionId, activeMs })
      .then((m) => magnum.handleEngineResponse(m, 'excited'))
      .catch(() => {})
    await endSession()
  }

  // Auto-advance from prompting to input
  useEffect(() => {
    if (state === 'prompting') {
      const timer = setTimeout(() => setState('input'), 800)
      return () => clearTimeout(timer)
    }
  }, [state])

  // Auto-advance from result only on correct answers
  useEffect(() => {
    if (state === 'result' && lastCorrect) {
      const timer = setTimeout(handleNext, 1500)
      return () => clearTimeout(timer)
    }
  }, [state, lastCorrect, handleNext])

  const handleSpellAgain = useCallback(() => {
    setLastAttempt(null)
    setLastCorrect(null)
    setInputKey(prev => prev + 1)
    setState('prompting')
    wordStartTime.current = Date.now()
    if (currentWord) {
      setTimeout(() => speakWord(currentWord.text), 300)
    }
  }, [currentWord])

  useEffect(() => {
    if (state === 'idle') {
      startGame()
    }
  }, [])

  useEffect(() => {
    if (isIdle && (state === 'input' || state === 'result')) {
      dismissIdle()
    }
  }, [state])

  if (!user || user.type !== 'child') return null

  if (state === 'loading') {
    return (
      <div className="py-16 text-center">
        <p className="text-lg text-muted-foreground">Loading spelling words...</p>
      </div>
    )
  }

  if (state === 'done') {
    const accuracy = localStats.total > 0 ? Math.round((localStats.correct / localStats.total) * 100) : 0
    return (
      <div className="py-8 text-center space-y-8">
        <div className="text-6xl">🎉</div>
        <h1 className="text-3xl font-bold">
          {masteredCount >= allWords.length ? 'All Words Mastered!' : 'Session Complete!'}
        </h1>
        <p className="text-muted-foreground">{listName}</p>
        <div className="grid grid-cols-3 gap-4 max-w-sm mx-auto">
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{masteredCount}</p>
            <p className="text-xs text-muted-foreground">Mastered</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold">{localStats.total}</p>
            <p className="text-xs text-muted-foreground">Attempts</p>
          </div>
          <div className="p-4 border border-border rounded-lg">
            <p className="text-2xl font-bold text-primary">{accuracy}%</p>
            <p className="text-xs text-muted-foreground">Accuracy</p>
          </div>
        </div>

        {/* Words summary */}
        <div className="max-w-sm mx-auto text-left space-y-2">
          {allWords.map(w => (
            <div key={w.id} className="flex items-center justify-between text-sm px-3 py-1.5 rounded-lg bg-accent/50">
              <span className="font-medium">{w.text}</span>
              <span>{w.mastered ? '✅' : w.dailyCleared ? '🎉' : w.dailyFailed ? '❌' : `${w.dailyCorrect}/${DAILY_GOAL}`}</span>
            </div>
          ))}
        </div>

        <div className="flex gap-4 justify-center pt-4">
          <button
            onClick={startGame}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition min-h-[44px]"
          >
            Practice Again
          </button>
          <Link
            to="/practice"
            className="px-6 py-3 border border-border rounded-xl font-bold hover:bg-accent transition inline-flex items-center min-h-[44px]"
          >
            Back to Exercises
          </Link>
        </div>
      </div>
    )
  }

  const diff = lastAttempt && currentWord ? diffLetters(lastAttempt, currentWord.text) : null

  return (
    <div className="text-center space-y-6">
      <EffectRenderer effects={activeEffects} onDone={() => setActiveEffects(null)} />

      {/* Top bar */}
      <div className="flex items-center justify-between">
        <Link
          to="/practice"
          className="text-sm text-muted-foreground hover:text-foreground transition"
        >
          &larr; Back
        </Link>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{masteredCount}/{allWords.length} mastered</span>
          <span className="text-primary font-medium">{localStats.correct} correct</span>
          {localStats.streak >= 3 && (
            <span className="text-amber-500 font-medium">🔥 {localStats.streak}</span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-accent rounded-full h-2">
        <div
          className="bg-primary h-2 rounded-full transition-all duration-300"
          style={{ width: `${(masteredCount / Math.max(allWords.length, 1)) * 100}%` }}
        />
      </div>

      {/* Current word hint and hear-again button */}
      {currentWord && (
        <div className="py-4 space-y-3">
          <button
            onClick={() => speakWord(currentWord.text)}
            className="mx-auto flex items-center gap-2 px-6 py-3 bg-accent rounded-xl hover:bg-accent/80 transition text-lg font-medium min-h-[44px]"
          >
            🔊 Hear the word
          </button>

          {currentWord.hint && (
            <p className="text-sm text-muted-foreground italic">"{currentWord.hint}"</p>
          )}

          {/* Daily progress dots */}
          <div className="flex items-center justify-center gap-1.5">
            {currentWord.mastered ? (
              <span className="text-xs font-medium text-primary">✅ Mastered</span>
            ) : currentWord.dailyCleared ? (
              <span className="text-xs font-medium text-primary">🎉 Cleared today!</span>
            ) : (
              <>
                {Array.from({ length: DAILY_GOAL }, (_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full transition-colors ${
                      i < (currentWord.dailyCorrect || 0)
                        ? currentWord.dailyFailed ? 'bg-amber-400' : 'bg-primary'
                        : 'bg-border'
                    }`}
                  />
                ))}
                {currentWord.dailyFailed && (
                  <span className="text-xs text-muted-foreground ml-1">missed today</span>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Input area */}
      {(state === 'prompting' || state === 'input') && (
        <SpellingInput
          key={inputKey}
          onSubmit={handleSubmit}
          disabled={state === 'prompting'}
          autoFocus
        />
      )}

      {/* Result display */}
      {state === 'result' && diff && currentWord && (
        <div className="space-y-4">
          <p className={`text-lg font-semibold ${lastCorrect ? 'text-primary' : 'text-amber-500'}`}>
            {lastCorrect ? '✓ Correct!' : '✗ Not quite'}
          </p>

          {/* Letter diff */}
          <div className="flex items-center justify-center gap-1.5 flex-wrap">
            {diff.map((d, i) => (
              <div key={i} className="flex flex-col items-center gap-1">
                <div
                  className={`w-10 h-12 flex items-center justify-center rounded-lg text-xl font-bold uppercase border-2 ${
                    d.correct
                      ? 'bg-green-50 border-green-300 text-green-700'
                      : d.letter
                        ? 'bg-red-50 border-red-300 text-red-700'
                        : 'bg-red-50 border-red-300 text-red-300'
                  }`}
                >
                  {d.letter || '?'}
                </div>
                {!d.correct && (
                  <span className="text-xs font-medium text-primary">{d.expected}</span>
                )}
              </div>
            ))}
          </div>

          {/* Show correct word — larger for wrong answers */}
          {!lastCorrect && (
            <p className="text-2xl font-bold text-foreground tracking-wide">
              {currentWord.text}
            </p>
          )}

          {/* Action buttons for wrong answers */}
          {!lastCorrect && (
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                onClick={handleSpellAgain}
                className="px-5 py-3 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition min-h-[44px]"
              >
                Spell Again
              </button>
              <button
                onClick={handleNext}
                className="px-5 py-3 border border-border rounded-xl font-bold hover:bg-accent transition min-h-[44px]"
              >
                Next Word
              </button>
            </div>
          )}
        </div>
      )}

      {/* End session button */}
      {(state === 'input' || state === 'prompting') && (
        <button
          onClick={handleEndSession}
          className="text-sm text-muted-foreground hover:text-foreground transition"
        >
          End session
        </button>
      )}
    </div>
  )
}

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}
